import { Clock, CheckCircle, DollarSign, Home, ThumbsUp, ShieldCheck, ScanLine, Calendar, Users, Ruler, CalendarClock, Hammer } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/bathroom.js';



export default function bathroom() {

  const service = { // change this along with the metadata
    title: 'Bathroom Remodeling',
    image: '/hero/best-bathroom-remodeling-in-los-angeles.webp',
    description: 'Upgrade your bathroom with Modern Build. Elegant designs, premium finishes, stress-free renovation. Free estimate: (323) 300-4130',
    link: '/services/bathroom-remodeling',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Bathroom Remodeling Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
<Head>
        <title>Bathroom Remodeling Experts | Free Estimate | Modern Build</title>
        <meta name="title" content="Bathroom Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="description" content="Upgrade your bathroom with Modern Build. Elegant designs, premium finishes, stress-free renovation. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="bathroom remodeling, home remodeling, adu conversion, garage conversion, los angeles bathroom remodeling, california home renovation, modern build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/bathroom-remodeling" />

        <meta property="og:title" content="Bathroom Remodeling Experts | Free Estimate | Modern Build" />
        <meta property="og:description" content="Upgrade your bathroom with Modern Build. Elegant designs, premium finishes, stress-free renovation. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-bathroom-remodeling-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/bathroom-remodeling" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bathroom Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Upgrade your bathroom with Modern Build. Elegant designs, premium finishes, stress-free renovation. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-bathroom-remodeling-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Bathroom Remodeling Services | Free Estimate | Modern Build',
                description: 'Luxury bathroom remodeling services designed for your lifestyle and style preferences. Modern Build makes your dream bathroom a reality.',
                slug: '/services/bathroom-remodeling'
              })
            )
          }}
        />
          
        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>
      <Header />
      <Hero category="bathroom" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="BATHROOM REMODELING SERVICES"
        title="Design. Build. Transform Your Bathroom."
        background="white"
        description="At Modern Build, we transform outdated bathrooms into beautiful, spa-like retreats tailored to your needs. Whether you want a sleek modern space or a classic luxury feel, our designers and craftsmen bring your vision to life with precision and care."
        extraText="From initial design to final walkthrough, we manage every detail to ensure your new bathroom is functional, stylish, and built to last — with custom solutions made just for you."
        phone="(323) 300-4130"
        image1="/split/complete-bathroom-remodeling-services-in-los-angeles-ca.webp"
        image2="/split/bathroom-remodeling-services-in-los-angeles-ca.webp"
      />

      <Benefits
        title="What’s Included In Your Bathroom Remodel?"
        subtitle="We handle everything from design to final inspection."
        items={[
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Custom Vanities & Storage Solutions",
          },
          {
            icon: <ScanLine className="w-8 h-8 text-primary" />,
            title: "Premium Tile & Flooring Installation",
          },
          {
            icon: <Clock className="w-8 h-8 text-primary" />,
            title: "Luxury Fixtures & Smart Accessories",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Plumbing & Electrical Upgrades",
          },
          {
            icon: <Calendar className="w-8 h-8 text-primary" />,
            title: "Efficient Project Scheduling",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Permits & City Inspections Managed",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Personalized Design Consultations",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Flexible Financing Available",
            href: "/financing",
          },
        ]}
      />

      <ContentBlock
        subtitle="FULL-SERVICE BATHROOM REMODELING"
        title="Custom Bathrooms Built Around Your Needs"
        description="We believe your bathroom should be both beautiful and practical. Modern Build offers complete remodeling services — from luxury walk-in showers to spa-worthy soaking tubs, elegant tiling, lighting, and more. We design bathrooms that fit your style and elevate your daily life."
        extraText="Our team handles permits, sourcing, layout optimization, and construction — ensuring a smooth, worry-free renovation experience."
        buttonText="Get a Free Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Process"
        subtitle="Step-by-step from concept to completion"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Consultation",
            description:
              "Every project starts with a conversation to understand your needs, style, and goals for your new bathroom.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Design & Material Selection",
            description:
              "Our designers help you choose layouts, tiles, fixtures, and finishes to create a bathroom that’s uniquely yours.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Planning & Timeline",
            description:
              "We prepare a clear plan, handle permits, and set a project timeline to keep everything on track and stress-free.",
          },
          {
            icon: <Hammer className="w-10 h-10 text-primary" />,
            title: "Build & Final Walkthrough",
            description:
              "Our expert craftsmen bring the design to life, ensuring every detail meets our high standards before your final walkthrough.",
          },
        ]}
      />

      <Split
        subtitle="TAILORED DESIGN EXPERIENCE"
        title="Built for You, Designed With You"
        background="white"
        description="Our team works closely with you to design a bathroom that reflects your lifestyle, taste, and functional needs. Whether you love sleek modern designs or classic luxury, we deliver personalized results."
        extraText="We provide 3D renderings, sample materials, and design boards to help you visualize your space before construction begins — with no surprises."
        phone="(323) 300-4130"
        image1="/split/best-bathroom-remodeling-in-los-angeles-ca.webp"
        image2="/split/bathroom-remodeling-in-los-angeles-ca.webp"
        reverse={true}
      />

      <CTASection
        title="Remodel Now. Pay Later."
        subtitle="100% Financing Available with $0 Down*"
        buttonText="Get Pre-Approved Today"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="bathroom"/>
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}